import { useCallback, useEffect, useState } from "react";
import { api, extractList, getApiError } from "@/api/client";
import { buildMapPoints, normalizeMapUser } from "@/utils/mapData";

const REFRESH_MS = 30_000;
const MAP_LIMIT = 1000;

const loadCompleteMapUsers = async () => {
  let skip = 0; let total = 0; let liveCount = 0; const items = [];
  for (let page = 0; page < 100; page += 1) {
    const { data } = await api.get("/admin/map", { params: { limit: MAP_LIMIT, skip, with_coords: false } });
    const pageItems = extractList(data, ["items"]);
    total = Number(data?.total ?? pageItems.length);
    liveCount = Number(data?.live_count ?? liveCount);
    items.push(...pageItems);
    const count = Number(data?.count ?? pageItems.length);
    if (!count || skip + count >= total) break;
    skip += MAP_LIMIT;
  }
  const unique = [...new Map(items.map((item) => [String(entityKey(item)), item])).values()];
  return { items: unique, total: total || unique.length, liveCount };
};

const entityKey = (item) => item?.user_id || item?.id || item?._id;

const mapState = (rawUsers, live, metadata) => {
  const users = rawUsers.map(normalizeMapUser);
  const points = buildMapPoints(users, live);
  return {
    users,
    live,
    points,
    stats: {
      total: metadata.total ?? users.length,
      creatorCount: users.filter((user) => user.isCreator).length,
      withCoordsCount: users.filter((user) => Boolean(user.position)).length,
      liveCount: metadata.liveCount ?? users.filter((user) => user.isLive).length,
    },
    source: metadata.source,
    loading: false,
    error: "",
    updatedAt: new Date(),
  };
};

export const useMapData = () => {
  const [state, setState] = useState({ points: [], users: [], live: [], stats: { total: 0, creatorCount: 0, withCoordsCount: 0, liveCount: 0 }, source: "map", loading: true, error: "", updatedAt: null });

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      let result;
      try {
        const mapUsers = await loadCompleteMapUsers();
        const liveResponse = await api.get("/admin/live");
        const live = extractList(liveResponse.data, ["live", "streams", "sessions"]);
        result = mapState(mapUsers.items, live, { total: mapUsers.total, liveCount: mapUsers.liveCount, source: "map" });
      } catch (mapError) {
        if (mapError?.response?.status !== 404) throw mapError;
        const [usersResponse, liveResponse] = await Promise.all([api.get("/admin/users?q="), api.get("/admin/live")]);
        const rawUsers = extractList(usersResponse.data, ["users"]);
        const live = extractList(liveResponse.data, ["live", "streams", "sessions"]);
        result = mapState(rawUsers, live, { total: rawUsers.length, liveCount: live.length, source: "legacy" });
      }
      setState(result);
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: getApiError(error) }));
    }
  }, []);

  useEffect(() => {
    load();
    const interval = window.setInterval(() => load({ silent: true }), REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [load]);

  return { ...state, reload: load };
};